import buildCapacityCells from '@/functions/buildCapacityCells';
import buildCapacityDefaultTotals from '@/functions/buildCapacityDefaultTotals';
import buildCapacityOverrideTotals from '@/functions/buildCapacityOverrideTotals';
import buildConfigPerFiscalMonth from '@/functions/buildConfigPerFiscalMonth';
import type {CapacityCombined} from '@/types/CapacityCombined';
import type {CapacityConfig} from '@/types/CapacityConfig';
import type {CapacityLabel} from '@/types/CapacityLabel';
import type {Employee} from '@/types/Employee';

const buildCapacityTotals = (
    employees : Employee[],
    capacityConfig : CapacityConfig,
    allCapacityCombined : CapacityCombined[],
    fiscalMonth : string,
    capacityLabels : CapacityLabel[],
) : Record<string, number> => {
    const capacityTotals : Record<string, number> = {};
    const capacityYearMonthSplit = fiscalMonth.split('-');
    const yearMonthsDisplay = buildCapacityCells(
        parseInt(capacityYearMonthSplit[0]),
        parseInt(capacityYearMonthSplit[1])
    );

    for (const employee of employees) {
        const capacityCombined = allCapacityCombined.filter(cap => cap.associateID === employee.id);
        const uniqueLabelIds = [...new Set(capacityCombined.map(label => label.timeLabelID))];
        const uniqueLabels = capacityLabels
            .filter(label => uniqueLabelIds.includes(label.timeLabelID) && !label.locked);

        const lockedLabels = capacityLabels.filter(label => label.locked);

        const configPerFiscalMonth = buildConfigPerFiscalMonth(capacityConfig, yearMonthsDisplay);
        const internalTimeCommittments = buildCapacityOverrideTotals(yearMonthsDisplay, uniqueLabels, capacityCombined);
        const timeOffCommitments = buildCapacityDefaultTotals(yearMonthsDisplay, lockedLabels, capacityCombined);

        yearMonthsDisplay.map(capacityYearMonth => {
            if (!Object.prototype.hasOwnProperty.call(capacityTotals, capacityYearMonth)) {
                capacityTotals[capacityYearMonth] = 0;
            }

            const fiscalDays = configPerFiscalMonth[capacityYearMonth].fiscal;
            const internalTime = internalTimeCommittments[capacityYearMonth];
            const timeOff = timeOffCommitments[capacityYearMonth];
            const capacityPerDay = ((fiscalDays * 8) - internalTime) / fiscalDays;
            const total = (capacityPerDay * fiscalDays) - (timeOff / 8 * capacityPerDay);
            capacityTotals[capacityYearMonth] += total;
        });
    }

    return capacityTotals;
};

export default buildCapacityTotals;
